import { useNavigate } from 'react-router-dom';
import {
    Card as MuiCard,
    Box,
    Stack,
    Chip,
    Typography,
    Avatar,
    Tooltip,
    alpha,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const AttachmentIcon = AttachFileIcon;
const CommentIcon = ChatBubbleOutlineIcon;
import type { Card, CardStatus } from '@/types';
import {
    isOverdue,
    formatLimitDate,
    getStatusLabel,
    PENDING,
    WORKING,
    DONE,
} from './cardStatus';

const STATUS_COLORS: Record<CardStatus, 'warning' | 'info' | 'success'> = {
    [PENDING]: 'warning',
    [WORKING]: 'info',
    [DONE]: 'success',
};

const STATUS_ACCENTS: Record<CardStatus, string> = {
    [PENDING]: '#f59e0b',
    [WORKING]: '#3b82f6',
    [DONE]: '#10b981',
};

interface CardTileProps {
    card: Card;
    isDragging?: boolean;
}

const initialsOf = (name: string | null | undefined): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
};

export const CardTile: React.FC<CardTileProps> = ({ card, isDragging = false }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/cards/${card.id}/show`, { replace: true });
    };
    const accent = STATUS_ACCENTS[card.status];
    const overdue = isOverdue(card.limit_date, card.status);

    return (
        <MuiCard
            onClick={handleClick}
            elevation={isDragging ? 8 : 1}
            sx={{
                position: 'relative',
                p: 1.5,
                pl: 2,
                cursor: 'pointer',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                background: theme =>
                    `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(accent, 0.03)} 100%)`,
                transition:
                    'transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 180ms ease, border-color 180ms ease',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: `linear-gradient(180deg, ${accent} 0%, ${alpha(accent, 0.6)} 100%)`,
                },
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme =>
                        `0 6px 16px ${alpha(theme.palette.common.black, 0.12)}`,
                    borderColor: alpha(accent, 0.5),
                },
                ...(isDragging && {
                    transform: 'rotate(1.5deg) scale(1.02)',
                    boxShadow: theme =>
                        `0 12px 28px ${alpha(theme.palette.common.black, 0.22)}`,
                }),
            }}
        >
            <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{ mb: 1 }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 700,
                        lineHeight: 1.35,
                        color: 'text.primary',
                        flex: 1,
                    }}
                >
                    {card.title}
                </Typography>
                <Chip
                    label={getStatusLabel(card.status)}
                    size="small"
                    color={STATUS_COLORS[card.status]}
                    sx={{
                        fontWeight: 700,
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                    }}
                />
            </Stack>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 1 }}
            >
                <Stack direction="row" spacing={0.75} alignItems="center">
                    {card.assigned_to_name && (
                        <Tooltip title={card.assigned_to_name} arrow>
                            <Avatar
                                sx={{
                                    width: 22,
                                    height: 22,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    bgcolor: alpha(accent, 0.18),
                                    color: accent,
                                }}
                            >
                                {initialsOf(card.assigned_to_name)}
                            </Avatar>
                        </Tooltip>
                    )}
                    {card.limit_date && (
                        <Chip
                            label={formatLimitDate(card.limit_date)}
                            size="small"
                            color={overdue ? 'error' : 'default'}
                            variant={overdue ? 'filled' : 'outlined'}
                            sx={{
                                fontSize: '0.68rem',
                                height: 20,
                                ...(overdue && {
                                    animation: 'cardTileOverduePulse 1.8s ease-in-out infinite',
                                    '@keyframes cardTileOverduePulse': {
                                        '0%, 100%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.0)' },
                                        '50%': { boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.18)' },
                                    },
                                }),
                            }}
                        />
                    )}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    {(card.attachment_count ?? 0) > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                                fontSize: '0.72rem',
                                color: 'text.secondary',
                            }}
                        >
                            <AttachmentIcon sx={{ fontSize: '0.9rem' }} />
                            {card.attachment_count}
                        </Box>
                    )}
                    {(card.comment_count ?? 0) > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                                fontSize: '0.72rem',
                                color: 'text.secondary',
                            }}
                        >
                            <CommentIcon sx={{ fontSize: '0.9rem' }} />
                            {card.comment_count}
                        </Box>
                    )}
                </Stack>
            </Stack>
        </MuiCard>
    );
};

CardTile.displayName = 'CardTile';
